import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import {
  Box,
  Button,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  Typography
} from '@mui/material'
import { useQuery } from 'react-query'
import { TreeViewCheck } from 'src/components/uiElements/styledComponents/TreeViewCheck'
import { TextInput, useListContext, useTranslate, DateTimeInput, SelectInput, DateInput } from 'react-admin'
import ContentFilter from '@mui/icons-material/FilterList'
import isEqual from 'lodash/isEqual'
import { SplitByCharacterWithPosition } from 'src/helper/splitByCharacter'
import { supplierServices } from 'src/services/reservation/supplier/supplier.service'
import { Close, HighlightOff, Search } from '@mui/icons-material'
import get from 'lodash/get'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'

export const SupplierBillingFilters = ({ listFilters }) => {
  const { displayedFilters = {}, setFilters, showFilter } = useListContext()
  const translate = useTranslate()
  const hasDisplayedFilters = !isEqual(displayedFilters, {})
  const [open, setOpen] = useState(false)
  const anchorEl = useRef()

  const handleClickButton = useCallback(
    (event) => {
      // This prevents ghost click.
      event.preventDefault()
      setOpen(true)
      anchorEl.current = event.currentTarget
    },
    [anchorEl, setOpen]
  )

  const handleRequestClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleShow = useCallback(
    ({ source, defaultValue }) => {
      showFilter(source, defaultValue === '' ? undefined : defaultValue)
      setOpen(false)
    },
    [showFilter, setOpen]
  )

  return (
    <Box>
      <Button
        className='add-filter'
        label='ra.action.add_filter'
        aria-haspopup='true'
        onClick={handleClickButton}
        size='small'
      >
        <ContentFilter /> {translate('ra.action.add_filter')}
      </Button>
      <Menu open={open} anchorEl={anchorEl.current} onClose={handleRequestClose}>
        {Array.isArray(listFilters) &&
          listFilters.map(
            (item, index) =>
              !displayedFilters?.[item.source] && (
                <MenuItem
                  onClick={() => {
                    setOpen(false)
                    handleShow({ source: item.source })
                  }}
                  key={index}
                >
                  {translate(`${item.name}`)}
                </MenuItem>
              )
          )}

        {hasDisplayedFilters && (
          <MenuItem onClick={() => setFilters({}, {}, false)}>
            {translate('ra.action.remove_all_filters')}
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

const assignIdToValue = (objects, labelField) => {
  if (Array.isArray(objects)) {
    return objects.map((obj) => {
      const newObj = {
        id: obj?.id,
        fiscal_data_relation: obj?.fiscal_data_relation,
        label: obj?.label,
        token: obj?.token,
        fantasy_name: obj?.fantasy_name,
        short_name: obj?.short_name,
        long_name: obj?.long_name,
        value: obj?.value,
        children: obj?.children || null,
      }

      // Usar 'get' para acceder a propiedades anidadas
      newObj.label = labelField
        ?.map((field) => get(obj, field))
        .filter((elem) => elem)
        .join(' - ')
      if (newObj.id !== undefined) {
        newObj.value = String(newObj.id) + '__' + newObj.label
      }

      if (newObj.children && newObj.children.length > 0) {
        newObj.children = assignIdToValue(newObj.children, labelField)
      } else {
        newObj.children = null
      }

      return newObj
    })
  }
  return null
}

const getDefaultValueTreeView = (data, value) => {
  let valueAux = []
  data.forEach((elem) => {
    if (elem.id === value) {
      valueAux.push(elem.value)
    } else if (elem.children && elem.children.length > 0) {
      valueAux = valueAux.concat(getDefaultValueTreeView(elem.children, value))
    }
  })

  return valueAux
}

const filterTreeView = (data, value, labelField) => {
  let expanded = []

  if (value) {
    const dataAux = []
    data.forEach((elem) => {
      const [filteredChildren, expandedChildren] = elem.children
        ? filterTreeView(elem.children, value, labelField)
        : [[], []]
      if (filteredChildren.length > 0) {
        elem.children = filteredChildren
        dataAux.push(elem)
        expanded = expanded.concat([elem.value, expandedChildren]).flat()
      } else if (elem[labelField]?.toLowerCase()?.includes(value)) {
        dataAux.push(elem)
      }
    })

    return [dataAux, expanded]
  }

  return [data, expanded]
}

const getId = (value) => {
  if (value && value.length) {
    return value.map((value) => SplitByCharacterWithPosition(value, '__', 0)).join(',')
  }
}

const getLabel = (value, emptyText) => {
  if (value && value.length) {
    return value.map((value) => SplitByCharacterWithPosition(value, '__', 1)).join(', ')
  }

  return emptyText
}

export const TreeViewInput = ({
  queryName,
  fieldName,
  modalTitle,
  buttonText,
  labelField,
  queryFn,
  limitOptions = 30,
}) => {
  const options = useQuery(queryName, () => queryFn())
  const translate = useTranslate()
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState([])
  const [currentOption, setCurrentOption] = useState([])
  const [formatedOptions, setFormatedOptions] = useState([])
  const { setValue } = useFormContext()
  const { filterValues } = useListContext()

  useEffect(() => {
    if (open) {
      setFormatedOptions(assignIdToValue(options.data.slice(0, limitOptions), labelField))
      setExpanded([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!options.isLoading) {
      const nodes = assignIdToValue(options.data.slice(0, limitOptions), labelField)
      const defaultValues = []
      filterValues[fieldName]?.split(',')?.forEach((elem) => {
        const id = parseInt(elem) || null
        if (id) {
          const defaultValue = getDefaultValueTreeView(nodes, id)
          defaultValues.push(defaultValue)
        }
      })
      setCurrentOption(defaultValues.flat())
      setFormatedOptions(nodes)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.isLoading])

  useEffect(() => {
    if (currentOption) {
      setValue(fieldName, getId(currentOption))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOption])

  const onSearch = (value) => {
    if (value.length >= 3) {
      const nodes = assignIdToValue(options.data, labelField)
      const [filteredNodes, expandedNodes] = filterTreeView(nodes, value.toLowerCase(), 'label')
      setFormatedOptions(filteredNodes)
      setExpanded(expandedNodes)
    } else {
      // Si la búsqueda es menos de 3 caracteres, muestra solo las opciones seleccionadas o resetea a la vista inicial
      setFormatedOptions(
        currentOption.length
          ? currentOption
          : assignIdToValue(options.data.slice(0, limitOptions), labelField)
      )
    }
  }

  const onCloseModal = () => {
    setOpen(false)
  }

  return (
    <>
      <Dialog open={open} maxWidth='md' fullWidth onClose={onCloseModal}>
        <DialogTitle display='flex' justifyContent='space-between'>
          {modalTitle}
          <IconButton size='small' onClick={onCloseModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size='small'
            placeholder={translate('ra.message.select_at_least', { num: 3 })}
            style={{ marginBottom: 24 }}
            onChange={(e) => e.target.value.length >= 3 && onSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          {formatedOptions.length >= limitOptions && (
            <Typography variant='caption' display='block' style={{ color: 'gray', marginLeft: 10 }}>
              vista previa de resultados ({limitOptions}).
            </Typography>
          )}
          <TreeViewCheck
            checked={currentOption}
            setChecked={setCurrentOption}
            nodes={formatedOptions}
            setExpanded={setExpanded}
            expanded={expanded}
            onlyOne={false}
            noCascade={false}
            activeHistorical={true}
            allNodes={assignIdToValue(options.data, labelField)}
          />
        </DialogContent>
      </Dialog>

      <Box width={'100%'}>
        {options.isLoading ? (
          <Skeleton variant='rectangular' width={'250px'} height={'40px'} />
        ) : (
          <Button
            sx={{
              width: '100%',
              height: '40px',
            }}
            onClick={() => setOpen(true)}
            variant='outlined'
          >
            <span style={{ textWrap: 'nowrap', textOverflow: 'ellipsis', overflowX: 'hidden' }}>
              {getLabel(currentOption, buttonText)}
            </span>
          </Button>
        )}
      </Box>
    </>
  )
}

const RemoveFilterField = ({ children, prop, fields, width = null, maxWidth = null, minWidth = null }) => {
  const { displayedFilters = {}, setFilters } = useListContext()
  const { setValue, getValues } = useFormContext()

  const onClickButton = () => {
    const copy = { ...displayedFilters }
    delete copy[prop]

    if (Array.isArray(fields) && fields.length > 0) {
      fields.forEach((field) => setValue(field, undefined))

      let values = getValues()
      setFilters(values, copy, false)
    }
  }

  return (
    <Box
      display={'flex'}
      alignItems={'flex-start'}
      marginRight={5}
      width={width}
      maxWidth={maxWidth}
      minWidth={minWidth}
    >
      <Box width={'34px'} paddingTop={1}>
        <IconButton size='small' onClick={onClickButton}>
          <HighlightOff />
        </IconButton>
      </Box>
      <Box width={'calc(100% - 34px)'} display={'flex'} minHeight={'60px'} height={'auto'}>
        {children}
      </Box>
    </Box>
  )
}

const SupplierInput = ({ label, source }) => {
  const [loadingSupplierList, setLoadingSupplierList] = useState(false)
  const [supplierList, setSupplierList] = useState([])

  const getSupplier = async (name) => {
    try {
      setLoadingSupplierList(true)
      const allCountries = await supplierServices.getByParam({ name })
      setSupplierList(allCountries)
      setLoadingSupplierList(false)
    } catch (error) {
      setSupplierList([])
      setLoadingSupplierList(false)
    }
  }

  return (
    <AutocompleteInput
        label={label}
        useOptionValue = {true}
        source={source}
        options={supplierList}
        loading={loadingSupplierList}
        optionsText={['name']}
        onSearch={getSupplier}
        resetOnLoad={false}
        style={{width:'100%'}}
    />
  )
}

export const PostFilterFormSupplierBilling = () => {
  const { displayedFilters = {}, filterValues, setFilters } = useListContext()
  const translate = useTranslate()
  const form = useForm()
  const documenTypeOptions = ['invoice', 'debit_note', 'credit_note']

  useEffect(() => {
    Object.entries(filterValues).forEach((entry) => {
      const [key, value] = entry
      form.setValue(key, value)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  useEffect(() => {
    if (displayedFilters && Object.keys(displayedFilters).length === 0) {
      form.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedFilters])

  const onSubmit = (values) => {
    if (Object.keys(values).length > 0) {

      setFilters(values, displayedFilters, false)
    }
  }

  const resetFilter = () => {
    setFilters({}, {}, false)
  }

  const areFilterActivate = (displayedFilters) => {
    if (displayedFilters && Object.keys(displayedFilters).length > 0) {
      const props = Object.values(displayedFilters)
      return props.some((prop) => prop === true)
    }
    return false
  }

  function convertToUTC(dateString) {
    return dateString
  }

  function convertFromUTC(dateString) {
    return dateString
  }

  return (
    <>
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center' mb={1} height={'auto'}>
          <Box width={'calc(100% - 156px)'} display={'flex'} flexWrap={'wrap'}>
            {displayedFilters?.date && (
              <RemoveFilterField prop={'date'} fields={['date__gte', 'date__lte']} width={500}>
                <DateInput
                  label={translate('ra.field.from')}
                  fullWidth
                  source='date__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.to')}
                  fullWidth
                  source='date__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.created_at && (
              <RemoveFilterField prop={'created_at'} fields={['created_at__gte', 'created_at__lte']} width={500}>
                <DateTimeInput
                  label={translate('ra.field.from')}
                  fullWidth
                  source='created_at__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateTimeInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.to')}
                  fullWidth
                  source='created_at__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.due_date && (
              <RemoveFilterField prop={'due_date'} fields={['due_date__gte', 'due_date__lte']} width={500}>
                <DateInput
                  label={translate('ra.field.start_due_date')}
                  fullWidth
                  source='due_date__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.end_due_date')}
                  fullWidth
                  source='due_date__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.document_type && (
              <RemoveFilterField prop={'document_type'} fields={['document_type']} width={300}>
                <SelectInput
                  source='document_type'
                  fullWidth
                  choices={documenTypeOptions.map(option=>({
                      id:option,
                      name:translate(`ra.field.${option}`)
                  }))}
                  label={translate('ra.field.document_type')}
              />
              </RemoveFilterField>
            )}
            {displayedFilters?.supplier_id && (
              <RemoveFilterField prop={'supplier_id'} fields={['supplier_id']} width={300}>
                <SupplierInput label={translate('ra.field.supplier')} source={'supplier_id'}/>
              </RemoveFilterField>
            )}
            {displayedFilters?.document_number__ilike && (
              <RemoveFilterField prop={'document_number__ilike'} fields={['document_number__ilike']} width={300}>
                <TextInput
                  label={translate('ra.field.document_number')}
                  fullWidth
                  source='document_number__ilike'
                />
              </RemoveFilterField>
            )}
            
          </Box>
          <Box
            width={'156px'}
            display={'flex'}
            flexWrap={'wrap'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
          >
            {areFilterActivate(displayedFilters) && (
              <>
                <Box mr={2} paddingY={'10px'}>
                  <Button variant='outlined' color='primary' type='submit'>
                    {translate('ra.action.filter')}
                  </Button>
                </Box>
                <Box paddingY={'10px'}>
                  <Button variant='outlined' onClick={resetFilter}>
                    {translate('ra.action.close')}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </form>
      </FormProvider>
    </>
  )
}
